import React, { useState, useEffect, useRef } from 'react';
import { X, Send, MessageCircle, Loader2, Minimize2 } from 'lucide-react';
import { Button } from './ui/button';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const GurujiChatWidget = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [messages, setMessages] = useState([
    {
      role: 'assistant',
      content: '🙏 Namaste! I am Guruji AI - India\'s No.1 AI Platform for Government Exam Preparation. Ask me any doubt about SSC, Banking, or RBI exams - no login required!'
    }
  ]);
  const [inputMessage, setInputMessage] = useState('');
  const [loading, setLoading] = useState(false);
  const messagesEndRef = useRef(null);

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  const handleSendMessage = async (e) => {
    e.preventDefault();

    if (!inputMessage.trim()) return;

    const userMessage = inputMessage.trim();
    setInputMessage('');

    // Add user message to chat
    setMessages(prev => [...prev, { role: 'user', content: userMessage }]);
    setLoading(true);

    try {
      // Prepare conversation history (last 10 messages)
      const conversationHistory = messages.slice(-10).map(msg => ({
        role: msg.role,
        content: msg.content
      }));

      // Use public endpoint - no authentication required
      const response = await fetch(`${API_URL}/api/ai/chatbot-public`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          message: userMessage,
          conversation_history: conversationHistory
        })
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.detail || 'Failed to get response');
      }

      // Add AI response to chat
      setMessages(prev => [...prev, { role: 'assistant', content: data.response }]);
    } catch (error) {
      console.error('Chat error:', error);
      setMessages(prev => [...prev, {
        role: 'assistant',
        content: '❌ Sorry, I encountered an error. Please try again.'
      }]);
    } finally {
      setLoading(false);
    }
  };

  const toggleChat = () => {
    setIsOpen(!isOpen);
  };

  return (
    <>
      {/* Floating Chat Button - Touchpoint 3 */}
      {!isOpen && (
        <div className="fixed bottom-6 right-6 z-50 flex flex-col items-end gap-2">
          {/* Badge */}
          <div className="bg-gradient-to-r from-yellow-400 to-yellow-500 text-purple-900 px-4 py-2 rounded-full shadow-lg text-xs font-bold animate-bounce">
            🏆 India's No.1 AI
          </div>
          
          {/* Chat Button */}
          <button
            onClick={toggleChat}
            className="w-16 h-16 bg-gradient-to-br from-purple-600 to-red-600 text-white rounded-full shadow-2xl hover:shadow-purple-500/50 hover:scale-110 transition-all duration-300 flex items-center justify-center group relative"
            aria-label="Open Guruji AI Chat"
          >
            <MessageCircle size={28} className="group-hover:rotate-12 transition-transform" />
            <span className="absolute -top-1 -right-1 w-4 h-4 bg-green-500 rounded-full border-2 border-white animate-pulse"></span>
          </button>
        </div>
      )}

      {/* Chat Window */}
      {isOpen && (
        <div className="fixed bottom-6 right-6 z-50 w-96 h-[600px] flex flex-col shadow-2xl rounded-2xl overflow-hidden animate-in slide-in-from-bottom-4">
          <Card className="w-full h-full flex flex-col border-2 border-purple-200">
            {/* Header */}
            <CardHeader className="bg-gradient-to-r from-purple-600 to-red-600 text-white p-4 flex-shrink-0">
              <div className="flex items-center justify-between">
                <div className="flex items-center space-x-2">
                  <div className="w-10 h-10 bg-white rounded-full flex items-center justify-center">
                    <span className="text-2xl">🙏</span>
                  </div>
                  <div>
                    <div className="flex items-center space-x-2">
                      <CardTitle className="text-white text-lg font-bold">Guruji AI</CardTitle>
                      <span className="bg-yellow-400 text-purple-900 text-[10px] font-bold px-2 py-0.5 rounded-full">🏆 #1</span>
                    </div>
                    <p className="text-xs text-purple-100">India's No.1 AI Study Assistant</p>
                  </div>
                </div>
                <div className="flex space-x-2">
                  <button
                    onClick={toggleChat}
                    className="text-white hover:bg-white/20 p-2 rounded-full transition-colors"
                    aria-label="Minimize chat"
                  >
                    <Minimize2 size={18} />
                  </button>
                  <button
                    onClick={toggleChat}
                    className="text-white hover:bg-white/20 p-2 rounded-full transition-colors"
                    aria-label="Close chat"
                  >
                    <X size={18} />
                  </button>
                </div>
              </div>
            </CardHeader>

            {/* Messages Area */}
            <CardContent className="flex-1 overflow-y-auto p-4 bg-gray-50 space-y-4">
              {messages.map((msg, idx) => (
                <div
                  key={idx}
                  className={`flex ${msg.role === 'user' ? 'justify-end' : 'justify-start'}`}
                >
                  <div
                    className={`max-w-[80%] p-3 rounded-2xl ${
                      msg.role === 'user'
                        ? 'bg-red-600 text-white rounded-br-none'
                        : 'bg-white text-gray-800 shadow-sm rounded-bl-none border'
                    }`}
                  >
                    {msg.role === 'assistant' && (
                      <div className="flex items-center mb-1">
                        <span className="text-sm font-semibold text-purple-600">🙏 Guruji</span>
                      </div>
                    )}
                    <p className="text-sm whitespace-pre-wrap leading-relaxed">{msg.content}</p>
                  </div>
                </div>
              ))}

              {loading && (
                <div className="flex justify-start">
                  <div className="bg-white p-3 rounded-2xl rounded-bl-none shadow-sm border">
                    <Loader2 className="animate-spin text-purple-600" size={20} />
                  </div>
                </div>
              )}

              <div ref={messagesEndRef} />
            </CardContent>

            {/* Input Area */}
            <div className="p-4 bg-white border-t flex-shrink-0">
              <form onSubmit={handleSendMessage} className="flex space-x-2">
                <input
                  type="text"
                  value={inputMessage}
                  onChange={(e) => setInputMessage(e.target.value)}
                  placeholder="Ask your doubt..."
                  disabled={loading}
                  className="flex-1 px-4 py-2 border border-gray-300 rounded-full focus:ring-2 focus:ring-purple-500 focus:border-transparent disabled:bg-gray-100 text-sm"
                />
                <Button
                  type="submit"
                  disabled={loading || !inputMessage.trim()}
                  className="bg-gradient-to-r from-purple-600 to-red-600 hover:from-purple-700 hover:to-red-700 rounded-full p-2 w-10 h-10"
                >
                  <Send size={18} />
                </Button>
              </form>
              <p className="text-xs text-gray-500 text-center mt-2">
                💬 Free for everyone - No login required!
              </p>
            </div>
          </Card>
        </div>
      )}
    </>
  );
};

export default GurujiChatWidget;
